import { memo, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getTxCustomersByRole } from '@features/components/CaseFormBlocks/utils';
import { useTXCenterByIdWithTXUsersQuery } from '@/queries/tx-centers';
import { CustomerRole } from '@/api/apis/customer';
import { ServiceType } from '@/api/apis/case';
import { CaseTxCenterFormType } from '../../CaseOverview/components/Tabs/CaseDetails/components/EditCaseForms/EditCaseTxCenter/config';
import { CaseCoordinatorInput } from './CaseCoordinatorInput';
import { CaseSurgeonInput } from './CaseSurgeonInput';

type CaseTxContactsProps = {
  hasPermission?: boolean;
  txUserDisabled?: boolean;
  serviceType?: ServiceType;
};

export const CaseTxContacts = memo(({ hasPermission, txUserDisabled, serviceType }: CaseTxContactsProps) => {
  const { control } = useFormContext<CaseTxCenterFormType>();
  const [txCenterId, receivingSurgeonId, coordinatorId, opoRequest, selectedServiceType] = useWatch({
    control,
    name: ['txCenterId', 'receivingSurgeonId', 'coordinatorId', 'opoRequest', 'serviceType'],
  });

  const { data } = useTXCenterByIdWithTXUsersQuery(txCenterId);

  const receivingSurgeonsList = useMemo(() => {
    return getTxCustomersByRole(data?.txUsers, CustomerRole.ReceivingSurgeon, txCenterId);
  }, [data?.txUsers, txCenterId]);

  const coordinatorsList = useMemo(() => {
    return getTxCustomersByRole(data?.txUsers, CustomerRole.TxCenterCoordinator, txCenterId);
  }, [data?.txUsers, txCenterId]);

  const currentCoordinator = useMemo(() => {
    return coordinatorsList.find((c) => c.id === coordinatorId);
  }, [coordinatorId, coordinatorsList]);

  const currentSurgeon = useMemo(() => {
    return receivingSurgeonsList.find((s) => s.id === receivingSurgeonId);
  }, [receivingSurgeonsList, receivingSurgeonId]);

  if (serviceType === ServiceType.DirectAcquisition || selectedServiceType === ServiceType.DirectAcquisition) {
    return null;
  }

  return (
    <div className="flex gap-4">
      <div className="flex-[50%] truncate">
        <CaseSurgeonInput
          contactsList={receivingSurgeonsList}
          currentSurgeon={currentSurgeon}
          hasPermission={hasPermission}
          disabled={txUserDisabled || !txCenterId}
        />
      </div>
      <div className="flex-[50%] truncate">
        <CaseCoordinatorInput
          txCenterId={txCenterId}
          isOpoRequest={opoRequest}
          contactsList={coordinatorsList}
          currentCoordinator={currentCoordinator}
          hasPermission={hasPermission}
          disabled={txUserDisabled || !txCenterId}
        />
      </div>
    </div>
  );
});
